import React, { useEffect, useState } from 'react';

export type OperationType = 
  | "protectedColumns"
  | "excludedColumns"
  | 'fillMissing'

interface OperationConfig {
  type: OperationType;
  target: string[];
  params?: {
    method?: string;
    value?: number | string;
  };
}

interface CleaningOperationsProps {
  operations: OperationConfig[];
  onAdd: (operation: OperationConfig) => void;
  onRemove: (index: number) => void;
  columns: string[];
}

const operationOptions = [
  { value: 'fillMissing', label: 'Handle Missing Values' },
  { value: 'protectedColumns', label: 'Protect Features From Removal' },
  { value: 'excludedColumns', label: 'Force Remove Features' }
  // { value: 'normalize', label: 'Normalize Data' },
  // { value: 'standardize', label: 'Standardize Data' },
  // { value: 'encode', label: 'Encode Categories' }
] as const;

const CleaningOperations: React.FC<CleaningOperationsProps> = ({
  operations,
  onAdd,
  onRemove,
  columns
}) => {
  const [selectedType, setSelectedType] = useState<OperationType>('fillMissing');
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [params, setParams] = useState<Record<string, any>>({});
  const [addButtonClicked, setAddButtonClicked] = useState<boolean>(false);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
        // ToDo: needs improvement
        // Check if "All" is selected
        if (selectedOptions.includes("All")) {
            // If "All" is selected, toggle the selection
            if (selectedColumns.includes("All")) {
                // If "All" is already selected, deselect all
                setSelectedColumns([]);
            } else {
                // If "All" is not selected, select all columns
                setSelectedColumns(columns);
            }
        } else {
            // Handle individual column selection
            setSelectedColumns(prevSelected => {
                // If the column is already selected, remove it
                if (prevSelected.includes(e.target.value)) {
                    return prevSelected.filter(selected => selected !== e.target.value);
                } else {
                    // If the column is not selected, add it
                    return [...prevSelected, e.target.value];
                }
            });
        }
    };

  useEffect(() => {
      if (selectedType == "fillMissing") {
        setParams({ method: 'mean' });
      }
      
      
  }, [selectedType,addButtonClicked]);

  const renderParamsForm = () => {
    switch (selectedType) {
      case 'fillMissing':
        return (
          <>
            <div className="form-group">
              <label>Strategy:</label>
              <select
                value={params.method || 'mean'}
                onChange={e => setParams({ ...params, method: e.target.value })}
              >
                <option value="mean">Mean</option>
                {/* <option value="median">Median</option>
                <option value="mode">Mode</option> */}
                {/* <option value="constant">Constant</option> */}
              </select>
            </div>
            {params.method === 'constant' && (
              <div className="form-group">
                <label>Fill Value:</label>
                <input
                  type="text"
                  value={params.value || ''}
                  onChange={e => setParams({ ...params, value: e.target.value })}
                />
              </div>
            )}
          </>
        );

    //   case 'handleOutliers':
    //     return (
    //       <div className="form-group">
    //         <label>Method:</label>
    //         <select
    //           value={params.method || 'iqr'}
    //           onChange={e => setParams({ ...params, method: e.target.value })}
    //         >
    //           {/* <option value="zscore">Z-Score</option> */}
    //           <option value="iqr">IQR</option>
    //         </select>
    //       </div>
    //     );

      // case 'normalize':
      // case 'standardize':
      //   return null;

      // case 'encode':
      //   return (
      //     <div className="form-group">
      //       <label>Method:</label>
      //       <select
      //         value={params.method || 'onehot'}
      //         onChange={e => setParams({ ...params, method: e.target.value })}
      //       >
      //         <option value="onehot">One-Hot Encoding</option>
      //         <option value="label">Label Encoding</option>
      //       </select>
      //     </div>
      //   );

      default:
        return null;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedColumns.length === 0) return;
    
    onAdd({
      type: selectedType,
      target: selectedColumns,
      params: params
    });
    setAddButtonClicked(prev => !prev)
    setSelectedColumns([]);
    setParams({});
  };

  return (
    <div className="preprocessing-operations">
      <h2>Cleaning Operations</h2>
      <p>Data will be automatically cleaned with a priority on optimal preparation for training, even if no operations are selected.</p>
      
      <form onSubmit={handleSubmit} className="operation-form">
        <div className="form-group">
          <label>Operation Type:</label>
          <select
            value={selectedType}
            onChange={e => setSelectedType(e.target.value as OperationType)}
          >
            {operationOptions.map(op => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Target Columns:</label>
          <select
            multiple
            value={selectedColumns}
            onChange={handleSelectChange}
          >
            <option value="All">All</option> 
            {columns.map(column => (
              <option key={column} value={column}>
                {column}
              </option>
            ))}
          </select>
        </div>

        {renderParamsForm()}

        <button 
          type="submit" 
          className="add-operation-btn"
          disabled={selectedColumns.length === 0}
        >
          Add Operation
        </button>
      </form>

      <div className="active-operations">
        <h3>Active Operations</h3>
        {operations.map((op, index) => (
          <div key={index} className="operation-item">
            <div className="operation-info">
              <span className="operation-type">
                {operationOptions.find(o => o.value === op.type)?.label}
              </span>
              <span className="operation-columns">
                Columns: {op.target.join(', ')}
              </span>
            </div>
            <button
              onClick={() => onRemove(index)}
              className="remove-operation-btn"
              aria-label="Remove operation"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CleaningOperations; 